<template>
  <MobileHeader :label="$t('nav.dashboard')" />
  <DashboardLayout>
    <template #main>
      <Graphs
        :organisationId="organisationId"
        :shaftId="shaftId"
      />
      <ReportsPanel
        :createPolicy="reportsStore.reportPolicies?.shaft_health_report_policy?.create"
        :fetchError="reportsStore.fetchReportsError"
        identifier="shaft"
        :loading="reportsStore.loading"
        :reports="reportsStore.reports"
        @delete-report="onReportItem"
      />
    </template>
    <template #sidebar>
      <DashboardInspections
        v-if="!warningSeeAll"
        inspectionPath="shaftV2Inspection"
        :inspectionsEndpoint="ENDPOINTS.inspections"
      />
      <DashboardIssues
        :highRiskRegionsEndpoint="ENDPOINTS.highRiskRegions"
        isStation
        regionsPathName="shaftV2IssuesTypeStations"
      />
      <DashboardIssues
        :highRiskRegionsEndpoint="ENDPOINTS.highRiskSets"
        isSet
        regionsPathName="shaftV2IssuesTypeSets"
      />
      <DashboardIssues
        v-if="hmeEnabled"
        :highRiskRegionsEndpoint="ENDPOINTS.highRiskHme"
        isHme
        regionsPathName="shaftV2IssuesTypeHmes"
      />
    </template>
  </DashboardLayout>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useChannel } from '@/functions/action_cable';
import { useShaftV2 } from '@/functions/use_remote_object';
import useAjax from '@/functions/use_ajax';

// Stores
import { useReportsStore } from '@/stores/reports/reportsStore';

//Components
import DashboardInspections from '@/components/dashboard/dashboard_inspections.vue';
import DashboardIssues from '@/components/dashboard/dashboard_issues.vue';
import DashboardLayout from '@/components/dashboard/dashboard_layout.vue';
import Graphs from '@/components/shafts/dashboard/graphs.vue';
import MobileHeader from '@/components/generic/mobile_header.vue';
import ReportsPanel from '@/components/dashboard/reports/reports_panel.vue';

const props = defineProps({
  organisationId: { type: String, required: true },
  shaftId: { type: String, required: true },
});

const ajax = useAjax();
const reportsStore = useReportsStore();

useChannel(
  {
    channel: 'ShaftsV2::ShaftHealthReportChannel',
    shaft_id: props.shaftId,
  },
  {
    received() {
      reportsStore.fetchReports(ENDPOINTS.value.shaftHealthReports);
    },
  }
);

const currentShaft = useShaftV2({
  organisationId: props.organisationId,
  shaftId: props.shaftId,
});

const highRiskRegions = ref(null);
const warningSeeAll = ref(false);

const hmeEnabled = computed(
  () => currentShaft.value?.policy?.show_heavy_mobile_equipment
);

const BASE_URL = computed(() => `/organisations/${props.organisationId}/shafts_v2/shafts/${props.shaftId}`);

const ENDPOINTS = computed(() => {
  return {
    highRiskHme: `${BASE_URL.value}/dashboard/high_risk_heavy_mobile_equipments`,
    highRiskRegions: `${BASE_URL.value}/dashboard/high_risk_regions`,
    highRiskSets: `${BASE_URL.value}/dashboard/high_risk_sets`,
    inspections: `${BASE_URL.value}/dashboard/inspections`,
    shaftHealthReportPolicies: `${BASE_URL.value}/dashboard/shaft_health_report_policies`,
    shaftHealthReports: `${BASE_URL.value}/shaft_health_reports`,
  };
});

onMounted(load);
watch(() => props.shaftId, load);

function load() {
  reportsStore.fetchReports(ENDPOINTS.value.shaftHealthReports);
  reportsStore.fetchReportPolicies(ENDPOINTS.value.shaftHealthReportPolicies);
  loadHighRiskRegions();
}

async function loadHighRiskRegions() {
  let { ok, data } = await ajax(ENDPOINTS.value.highRiskRegions);

  if (ok) {
    highRiskRegions.value = data;
  }
}

async function onReportItem(id) {
  reportsStore.deleteReport(id, `${ENDPOINTS.value.shaftHealthReports}/${id}`);
}
</script>
